import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { Dropdown } from 'react-bootstrap';
import AppContext from 'context/Context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faClock,
    faSave,
    faSpinner,
    faEdit,
    faTimes,
    faShieldAlt
} from '@fortawesome/free-solid-svg-icons';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'];

const CustomTimeToggle = React.forwardRef(({ value, disabled, isDark, placeholder, onClick, onChange, onBlur }, ref) => (
    <div
        ref={ref}
        className="position-relative d-inline-flex align-items-center"
        style={{ width: '135px' }}
    >
        <input
            type="text"
            className="form-control form-control-sm text-center"
            value={value}
            placeholder={placeholder}
            disabled={disabled}
            onClick={e => {
                if (!disabled && onClick) {
                    onClick(e);
                }
            }}
            onChange={onChange}
            onBlur={onBlur}
            maxLength={5}
            style={{
                paddingLeft: '32px',
                paddingRight: '10px',
                fontWeight: 600,
                fontSize: '0.86rem',
                letterSpacing: '0.04em',
                backgroundColor: isDark
                    ? (!disabled ? '#182635' : '#111827')
                    : (!disabled ? '#ffffff' : '#f1f5f9'),
                borderColor: isDark ? '#233142' : '#ced4da',
                color: isDark ? '#f9fafd' : '#0f172a',
                opacity: !disabled ? 1 : 0.65,
                cursor: disabled ? 'not-allowed' : 'pointer'
            }}
        />
        <FontAwesomeIcon
            icon={faClock}
            className="position-absolute"
            style={{
                left: '11px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: disabled
                    ? (isDark ? '#475569' : '#94a3b8')
                    : (isDark ? '#60a5fa' : '#2563eb'),
                fontSize: '0.84rem',
                pointerEvents: 'none'
            }}
        />
    </div>
));
CustomTimeToggle.displayName = 'CustomTimeToggle';

const BootstrapTimePicker = ({
    value = '',
    onChange,
    disabled = false,
    isDark = false,
    placeholder = '--:--'
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState(value || '');
    const hoursListRef = useRef(null);
    const minutesListRef = useRef(null);

    useEffect(() => {
        setInputValue(value || '');
    }, [value]);

    const [selectedHour, selectedMinute] = useMemo(() => {
        if (value && value.includes(':')) {
            const parts = value.split(':');
            return [parts[0].padStart(2, '0'), parts[1].padStart(2, '0')];
        }
        return ['', ''];
    }, [value]);

    useEffect(() => {
        if (isOpen) {
            const timer = setTimeout(() => {
                if (hoursListRef.current) {
                    const selectedEl = hoursListRef.current.querySelector('[data-selected="true"]');
                    if (selectedEl) {
                        selectedEl.scrollIntoView({ block: 'center' });
                    }
                }
                if (minutesListRef.current) {
                    const selectedEl = minutesListRef.current.querySelector('[data-selected="true"]');
                    if (selectedEl) {
                        selectedEl.scrollIntoView({ block: 'center' });
                    }
                }
            }, 60);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    const handleInputChange = e => {
        let val = e.target.value.replace(/[^0-9:]/g, '');
        if (val.length === 2 && !val.includes(':') && val.length > (inputValue || '').length) {
            val = val + ':';
        }
        setInputValue(val);
        if (/^([01]\d|2[0-3]):([0-5]\d)$/.test(val)) {
            onChange && onChange(val);
        }
    };

    const handleInputBlur = () => {
        if (!inputValue) {
            onChange && onChange('');
            return;
        }
        if (!/^([01]\d|2[0-3]):([0-5]\d)$/.test(inputValue)) {
            setInputValue(value || '');
        }
    };

    const handleSelectHour = hour => {
        const min = selectedMinute || '00';
        const newTime = `${hour}:${min}`;
        setInputValue(newTime);
        onChange && onChange(newTime);
    };

    const handleSelectMinute = minute => {
        const hr = selectedHour || '09';
        const newTime = `${hr}:${minute}`;
        setInputValue(newTime);
        onChange && onChange(newTime);
    };


    return (
        <div className="se-bootstrap-timepicker d-inline-block">
            <style>{`
                .se-time-scrollbar {
                    scrollbar-width: thin;
                    scrollbar-color: ${isDark ? '#2c3e55 #0b1727' : '#cbd5e1 #f1f5f9'};
                }
                .se-time-scrollbar::-webkit-scrollbar {
                    width: 6px;
                }
                .se-time-scrollbar::-webkit-scrollbar-track {
                    background: ${isDark ? '#0b1727' : '#f1f5f9'};
                    border-radius: 4px;
                }
                .se-time-scrollbar::-webkit-scrollbar-thumb {
                    background-color: ${isDark ? '#2c3e55' : '#cbd5e1'};
                    border-radius: 4px;
                }
                .se-time-scrollbar::-webkit-scrollbar-thumb:hover {
                    background-color: ${isDark ? '#3b82f6' : '#94a3b8'};
                }
                .se-time-scrollbar::-webkit-scrollbar-button {
                    display: none;
                    width: 0;
                    height: 0;
                }
            `}</style>
            <Dropdown
                show={isOpen}
                onToggle={nextShow => {
                    if (!disabled) {
                        setIsOpen(nextShow);
                    }
                }}
                autoClose="outside"
            >
                <Dropdown.Toggle
                    as={CustomTimeToggle}
                    value={inputValue}
                    disabled={disabled}
                    isDark={isDark}
                    placeholder={placeholder}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                />

                <Dropdown.Menu
                    align="start"
                    popperConfig={{
                        strategy: 'fixed',
                        modifiers: [
                            {
                                name: 'preventOverflow',
                                options: { boundary: 'viewport', padding: 8 }
                            },
                            {
                                name: 'flip',
                                options: { fallbackPlacements: ['top-start', 'bottom-end', 'top-end'] }
                            }
                        ]
                    }}
                    className="shadow-lg border p-2"
                    style={{
                        minWidth: '220px',
                        maxWidth: '240px',
                        backgroundColor: isDark ? '#121e2d' : '#ffffff',
                        borderColor: isDark ? '#233142' : '#e2e8f0',
                        color: isDark ? '#f9fafd' : '#0f172a',
                        borderRadius: '8px',
                        zIndex: 1060
                    }}
                >
                    {/* Header */}
                    <div
                        className="d-flex align-items-center justify-content-between pb-2 mb-2 border-bottom"
                        style={{ borderColor: isDark ? '#233142' : '#e2e8f0' }}
                    >
                        <div className="d-flex align-items-center gap-1">
                            <FontAwesomeIcon
                                icon={faClock}
                                style={{ color: isDark ? '#60a5fa' : '#2563eb', fontSize: '0.8rem' }}
                            />
                            <span className="fw-bold" style={{ fontSize: '0.74rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                                SELECCIONAR HORA
                            </span>
                        </div>
                        <span
                            className="badge px-2 py-1 fw-bold"
                            style={{
                                fontSize: '0.78rem',
                                backgroundColor: isDark ? 'rgba(37, 99, 235, 0.25)' : '#dbeafe',
                                color: isDark ? '#60a5fa' : '#1e40af'
                            }}
                        >
                            {inputValue || '--:--'}
                        </span>
                    </div>


                    {/* Columnas Horas y Minutos */}
                    <div className="row g-2 text-center my-1">
                        {/* Horas */}
                        <div className="col-6">
                            <div className="fw-semibold text-uppercase mb-1" style={{ fontSize: '0.68rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                                Hora
                            </div>
                            <div
                                ref={hoursListRef}
                                className="se-time-scrollbar d-flex flex-column gap-1 overflow-auto pe-1"
                                style={{
                                    maxHeight: '190px',
                                    scrollbarWidth: 'thin',
                                    scrollbarColor: isDark ? '#2c3e55 #0b1727' : '#cbd5e1 #f1f5f9'
                                }}
                            >
                                {HOURS.map(h => {
                                    const isSelected = selectedHour === h;
                                    return (
                                        <button
                                            key={h}
                                            type="button"
                                            data-selected={isSelected}
                                            className={`btn btn-sm py-1 px-0 border-0 rounded text-center ${isSelected ? 'btn-primary text-white fw-bold' : 'btn-falcon-default'}`}
                                            onClick={() => handleSelectHour(h)}
                                            style={{
                                                fontSize: '0.78rem',
                                                fontWeight: isSelected ? 700 : 500
                                            }}
                                        >
                                            {h}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Minutos */}
                        <div className="col-6" style={{ borderLeft: isDark ? '1px solid #233142' : '1px solid #e2e8f0' }}>
                            <div className="fw-semibold text-uppercase mb-1" style={{ fontSize: '0.68rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                                Minuto
                            </div>
                            <div
                                ref={minutesListRef}
                                className="se-time-scrollbar d-flex flex-column gap-1 overflow-auto pe-1"
                                style={{
                                    maxHeight: '190px',
                                    scrollbarWidth: 'thin',
                                    scrollbarColor: isDark ? '#2c3e55 #0b1727' : '#cbd5e1 #f1f5f9'
                                }}
                            >
                                {MINUTES.map(m => {
                                    const isSelected = selectedMinute === m;
                                    return (
                                        <button
                                            key={m}
                                            type="button"
                                            data-selected={isSelected}
                                            className={`btn btn-sm py-1 px-0 border-0 rounded text-center ${isSelected ? 'btn-primary text-white fw-bold' : 'btn-falcon-default'}`}
                                            onClick={() => handleSelectMinute(m)}
                                            style={{
                                                fontSize: '0.78rem',
                                                fontWeight: isSelected ? 700 : 500
                                            }}
                                        >
                                            {m}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div
                        className="pt-2 mt-2 border-top d-flex justify-content-between align-items-center"
                        style={{ borderColor: isDark ? '#233142' : '#e2e8f0' }}
                    >
                        <span style={{ fontSize: '0.68rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                            Formato 24 hrs
                        </span>
                        <button
                            type="button"
                            className="btn btn-sm btn-primary py-0 px-2"
                            style={{ fontSize: '0.74rem', borderRadius: '4px' }}
                            onClick={() => setIsOpen(false)}
                        >
                            Listo
                        </button>
                    </div>
                </Dropdown.Menu>
            </Dropdown>
        </div>
    );
};

const DIAS_SEMANA_DEFAULT = [
    { iDiaSemana: 1, vDiaSemana: 'Lunes', dHoraInicio: '09:00:00', dHoraFin: '18:00:00', bLaboral: true },
    { iDiaSemana: 2, vDiaSemana: 'Martes', dHoraInicio: '09:00:00', dHoraFin: '18:00:00', bLaboral: true },
    { iDiaSemana: 3, vDiaSemana: 'Miércoles', dHoraInicio: '09:00:00', dHoraFin: '18:00:00', bLaboral: true },
    { iDiaSemana: 4, vDiaSemana: 'Jueves', dHoraInicio: '09:00:00', dHoraFin: '18:00:00', bLaboral: true },
    { iDiaSemana: 5, vDiaSemana: 'Viernes', dHoraInicio: '09:00:00', dHoraFin: '18:00:00', bLaboral: true },
    { iDiaSemana: 6, vDiaSemana: 'Sábado', dHoraInicio: '09:00:00', dHoraFin: '14:00:00', bLaboral: true },
    { iDiaSemana: 7, vDiaSemana: 'Domingo', dHoraInicio: null, dHoraFin: null, bLaboral: false }
];

const BusinessHoursSettingsSection = () => {
    const { config: { isDark } = {} } = useContext(AppContext);
    const { token, nombreCompleto, idCatTipoUsuario } = useToken();
    const isAdmin = Number(idCatTipoUsuario) === 1;
    const { showSuccessToast, showErrorToast } = useToast();

    const [horarios, setHorarios] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const backupHorariosRef = useRef([]);

    // Cargar catálogo de horarios desde el backend
    const fetchHorarios = async () => {
        setIsLoading(true);
        try {
            const response = await fetch('/api/SupportEngine/GetHorarios', {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': 'Bearer ' + token
                }
            });

            if (response.ok) {
                const result = await response.json();
                const list = result.data || result;
                if (Array.isArray(list) && list.length > 0) {
                    setHorarios(list);
                } else {
                    setHorarios(DIAS_SEMANA_DEFAULT);
                }
            } else {
                setHorarios(DIAS_SEMANA_DEFAULT);
            }
        } catch (err) {
            console.error('Error al cargar horarios:', err);
            setHorarios(DIAS_SEMANA_DEFAULT);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (token && isAdmin) {
            fetchHorarios();
        }
    }, [token, isAdmin]);

    if (!isAdmin) {
        return (
            <div className="alert alert-warning p-4 rounded-3 text-center my-4">
                <FontAwesomeIcon icon={faShieldAlt} className="fa-2x mb-2 text-warning" />
                <h6 className="fw-bold mb-1">Acceso Restringido</h6>
                <p className="mb-0 small text-muted">
                    Esta sección de configuración de horarios es exclusiva para administradores del sistema.
                </p>
            </div>
        );
    }

    const handleChange = (index, field, value) => {
        setHorarios(prev => {
            const copy = [...prev];
            copy[index] = { ...copy[index], [field]: value };
            return copy;
        });
    };

    // Activar o desactivar el día (Laboral / Inactivo)
    const handleToggleLaboral = (index, checked) => {
        setHorarios(prev => {
            const copy = [...prev];
            const current = copy[index];
            copy[index] = {
                ...current,
                bLaboral: checked,
                dHoraInicio: checked ? (current.dHoraInicio || '09:00:00') : current.dHoraInicio,
                dHoraFin: checked ? (current.dHoraFin || (current.iDiaSemana === 6 ? '14:00:00' : '18:00:00')) : current.dHoraFin
            };
            return copy;
        });
    };

    // Iniciar edición
    const handleStartEdit = () => {
        backupHorariosRef.current = JSON.parse(JSON.stringify(horarios));
        setIsEditing(true);
    };

    // Cancelar edición y restaurar valores
    const handleCancelEdit = () => {
        if (backupHorariosRef.current.length > 0) {
            setHorarios(backupHorariosRef.current);
        }
        setIsEditing(false);
    };

    // Guardar edición de todos los horarios
    const handleSaveAll = async () => {
        setIsSaving(true);
        try {
            const requests = horarios.map(item => {
                const payload = {
                    idHorarioServicio: item.idHorarioServicio || null,
                    iDiaSemana: item.iDiaSemana,
                    vDiaSemana: item.vDiaSemana,
                    dHoraInicio: item.bLaboral ? (item.dHoraInicio ? item.dHoraInicio.substring(0, 8) : '09:00:00') : null,
                    dHoraFin: item.bLaboral ? (item.dHoraFin ? item.dHoraFin.substring(0, 8) : '18:00:00') : null,
                    bLaboral: !!item.bLaboral,
                    vUser: nombreCompleto || 'Agente'
                };

                return fetch('/api/SupportEngine/SaveHorario', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'Authorization': 'Bearer ' + token
                    },
                    body: JSON.stringify(payload)
                });
            });

            const responses = await Promise.all(requests);
            const allOk = responses.every(res => res.ok);

            if (allOk) {
                if (showSuccessToast) {
                    showSuccessToast('Horarios de atención actualizados con éxito.');
                }
                setIsEditing(false);
                await fetchHorarios();
            } else {
                if (showErrorToast) {
                    showErrorToast('Ocurrió un error al guardar uno o más horarios.');
                }
            }
        } catch (err) {
            if (showErrorToast) {
                showErrorToast('Error de conexión al guardar los horarios.');
            }
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="se-card-panel mb-4">
            {/* Header Sección */}
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div>
                    <h5 className="fw-bold mb-1 d-flex align-items-center gap-2" style={{ fontSize: '1.15rem', color: isDark ? '#f9fafd' : '#0f172a' }}>
                        <div
                            className="d-flex align-items-center justify-content-center rounded-circle"
                            style={{
                                width: '32px',
                                height: '32px',
                                backgroundColor: isDark ? 'rgba(37, 99, 235, 0.2)' : '#dbeafe',
                                color: isDark ? '#60a5fa' : '#2563eb'
                            }}
                        >
                            <FontAwesomeIcon icon={faClock} style={{ fontSize: '0.9rem' }} />
                        </div>
                        <span>Horarios de Atención y Servicio</span>
                    </h5>
                    <p className="small mb-0" style={{ fontSize: '0.84rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                        Configura los días laborales y rangos de atención del equipo de soporte para la respuesta automática y guardias.
                    </p>
                </div>

                {/* Botón Ternario de Edición */}
                <div className="d-flex align-items-center gap-2">
                    {isEditing && (
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-2"
                            onClick={handleCancelEdit}
                            disabled={isSaving}
                            style={{ fontSize: '0.84rem', borderRadius: '6px' }}
                        >
                            <FontAwesomeIcon icon={faTimes} />
                            <span>Cancelar</span>
                        </button>
                    )}

                    <button
                        type="button"
                        className={`btn btn-sm d-flex align-items-center gap-2 ${isEditing ? 'btn-success' : 'btn-primary'}`}
                        onClick={isEditing ? handleSaveAll : handleStartEdit}
                        disabled={isLoading || isSaving}
                        style={{ fontSize: '0.84rem', borderRadius: '6px' }}
                    >
                        {isSaving ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                <span>Guardando...</span>
                            </>
                        ) : isEditing ? (
                            <>
                                <FontAwesomeIcon icon={faSave} />
                                <span>Guardar Edición</span>
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faEdit} />
                                <span>Editar Horario</span>
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Tabla de Horarios */}
            {isLoading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status" style={{ width: '2rem', height: '2rem' }} />
                    <p className="small mt-2 mb-0" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>Cargando configuración de horarios...</p>
                </div>
            ) : (
                <div
                    className="table-responsive rounded-3 border shadow-xs"
                    style={{ backgroundColor: isDark ? '#121e2d' : '#ffffff', borderColor: isDark ? '#233142' : '#e2e8f0' }}
                >
                    <table className="table align-middle mb-0" style={{ borderCollapse: 'separate', borderSpacing: 0, backgroundColor: isDark ? '#121e2d' : '#ffffff' }}>
                        <thead>
                            <tr style={{ borderBottom: isDark ? '1px solid #233142' : '1px solid #e2e8f0' }}>
                                <th className="py-3 px-3 fw-bold text-uppercase" style={{ fontSize: '0.74rem', letterSpacing: '0.04em', color: isDark ? '#94a3b8' : '#64748b', width: '22%' }}>
                                    DÍA DE LA SEMANA
                                </th>
                                <th className="py-3 px-3 fw-bold text-uppercase" style={{ fontSize: '0.74rem', letterSpacing: '0.04em', color: isDark ? '#94a3b8' : '#64748b', width: '25%' }}>
                                    HORA INICIO
                                </th>
                                <th className="py-3 px-3 fw-bold text-uppercase" style={{ fontSize: '0.74rem', letterSpacing: '0.04em', color: isDark ? '#94a3b8' : '#64748b', width: '25%' }}>
                                    HORA FIN
                                </th>
                                <th className="py-3 px-3 fw-bold text-uppercase" style={{ fontSize: '0.74rem', letterSpacing: '0.04em', color: isDark ? '#94a3b8' : '#64748b', width: '18%' }}>
                                    ESTADO
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {horarios.map((item, idx) => {
                                const isLaboral = item.bLaboral;
                                const horaInicioValue = item.dHoraInicio ? item.dHoraInicio.substring(0, 5) : '';
                                const horaFinValue = item.dHoraFin ? item.dHoraFin.substring(0, 5) : '';

                                return (
                                    <tr
                                        key={item.iDiaSemana || idx}
                                        style={{ borderBottom: isDark ? '1px solid #233142' : '1px solid #f1f5f9' }}
                                    >
                                        {/* DÍA */}
                                        <td className="py-3 px-3 align-middle">
                                            <div className="fw-bold" style={{ fontSize: '0.92rem', color: isDark ? '#f9fafd' : '#0f172a' }}>
                                                {item.vDiaSemana}
                                            </div>
                                        </td>

                                        {/* HORA INICIO */}
                                        <td className="py-3 px-3 align-middle">
                                            <BootstrapTimePicker
                                                value={horaInicioValue}
                                                disabled={!isEditing || !isLaboral}
                                                isDark={isDark}
                                                placeholder="--:--"
                                                onChange={val => handleChange(idx, 'dHoraInicio', val ? `${val}:00` : null)}
                                            />
                                        </td>

                                        {/* HORA FIN */}
                                        <td className="py-3 px-3 align-middle">
                                            <BootstrapTimePicker
                                                value={horaFinValue}
                                                disabled={!isEditing || !isLaboral}
                                                isDark={isDark}
                                                placeholder="--:--"
                                                onChange={val => handleChange(idx, 'dHoraFin', val ? `${val}:00` : null)}
                                            />
                                        </td>

                                        {/* SWITCH ESTADO */}
                                        <td className="py-3 px-3 align-middle">
                                            <div className="form-check form-switch m-0 d-flex align-items-center gap-2">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    role="switch"
                                                    id={`switchDia_${item.iDiaSemana}`}
                                                    checked={isLaboral}
                                                    disabled={!isEditing}
                                                    onChange={e => handleToggleLaboral(idx, e.target.checked)}
                                                    style={{ cursor: isEditing ? 'pointer' : 'default', opacity: isEditing ? 1 : 0.7 }}
                                                />
                                                <label
                                                    className={`badge rounded-pill px-2 py-1 fw-semibold ${isLaboral ? 'bg-success' : 'bg-secondary'}`}
                                                    htmlFor={`switchDia_${item.iDiaSemana}`}
                                                    style={{ fontSize: '0.72rem', cursor: isEditing ? 'pointer' : 'default' }}
                                                >
                                                    {isLaboral ? 'Laboral' : 'Inactivo'}
                                                </label>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default BusinessHoursSettingsSection;
